import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { ArrowLeftOutlined, LockFilled } from '@ant-design/icons';
import {
  COLOR,
  FONT_SIZE,
  LAYOUT,
  RADIUS,
  SHADOW,
  SPACING,
} from '../../../constants/theme';
import { intercomService } from '../../appServices';

const DEFAULT_BAR_HEIGHT = 120;
const LAUNCHER_GAP = 12;

const BarSpacer = styled.div`
  height: ${({ height }) => height}px;
`;

const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: ${COLOR.surface};
  border-top: 1px solid ${COLOR.border};
  box-shadow: ${SHADOW.actionBar};
`;

const Inner = styled.div`
  max-width: ${LAYOUT.maxWidth};
  margin: 0 auto;
  padding: ${SPACING.md} ${LAYOUT.gutter}
    calc(${SPACING.lg} + env(safe-area-inset-bottom, 0px));
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm} ${SPACING.lg};
`;

// Between Back and Next on wider screens. On phones it is its own full-width
// line (label left, amount right) above the actions.
const AmountBlock = styled.div`
  @media (max-width: 480px) {
    order: -1;
    flex: 1 0 100%;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${SPACING.md};
  }
`;

const AmountLabel = styled.div`
  font-size: ${FONT_SIZE.hint};
  color: ${COLOR.textSecondary};
`;

const AmountValue = styled.div`
  font-size: ${FONT_SIZE.amount};
  font-weight: 700;
  color: ${COLOR.navy};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const AmountCurrency = styled.span`
  margin-left: 3px;
  font-size: ${FONT_SIZE.hint};
  font-weight: 500;
  color: ${COLOR.textSecondary};
`;

const Amount = ({ label, amount, currency }) => (
  <>
    <AmountLabel>{label}</AmountLabel>
    <AmountValue>
      {amount}
      {currency && <AmountCurrency>{currency}</AmountCurrency>}
    </AmountValue>
  </>
);

const BackButton = styled(Button)`
  padding: 0 6px;
  height: 40px;
  font-size: ${FONT_SIZE.value};
  font-weight: 500;
  color: ${COLOR.textSecondary};

  &:hover {
    color: ${COLOR.text};
  }
`;

const PrimaryButton = styled(Button)`
  height: 46px;
  padding: 0 ${SPACING.xxl};
  border: none;
  border-radius: ${RADIUS.md};
  font-size: 15px;
  font-weight: 700;

  /* Alone in the row (no Back or amount, e.g. Verify), it fills the bar. */
  &:only-child {
    flex: 1;
  }

  /* On phones it fills the actions line: beside Back, or the full width. */
  @media (max-width: 480px) {
    flex: 1;
    padding: 0 ${SPACING.lg};
  }
`;

const ConsentLine = styled.p`
  margin: ${SPACING.sm} 0 0;
  font-size: ${FONT_SIZE.hint};
  line-height: 1.45;
  text-align: center;
  color: ${COLOR.textSecondary};
`;

const SecureLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 9px;
  font-size: ${FONT_SIZE.hint};
  color: ${COLOR.textSecondary};
`;

// Keeps the page's end clear of the fixed bar, and the chat launcher above it.
const useBarHeight = (barRef) => {
  const [height, setHeight] = useState(DEFAULT_BAR_HEIGHT);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const update = () => setHeight(bar.offsetHeight);
    update();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(update);
    observer.observe(bar);

    return () => observer.disconnect();
  }, [barRef]);

  useEffect(() => {
    intercomService.setLauncherBottom(height + LAUNCHER_GAP);
  }, [height]);

  useEffect(() => () => intercomService.resetLauncherBottom(), []);

  return height;
};

const ActionBar = ({
  amountLabel,
  amount,
  currency,
  backLabel,
  onBack,
  nextLabel,
  onNext,
  isNextDisabled,
  isLoading,
  consentLine,
  secureLine,
}) => {
  const barRef = useRef(null);
  const barHeight = useBarHeight(barRef);

  return (
    <>
      <BarSpacer height={barHeight} />
      <Bar ref={barRef}>
        <Inner>
          <Row>
            {onBack && (
              <BackButton type="text" onClick={onBack}>
                <ArrowLeftOutlined />
                {backLabel || 'Back'}
              </BackButton>
            )}
            {amount && (
              <AmountBlock>
                <Amount
                  label={amountLabel}
                  amount={amount}
                  currency={currency}
                />
              </AmountBlock>
            )}
            <PrimaryButton
              type="primary"
              onClick={onNext}
              disabled={isNextDisabled}
              loading={isLoading}
            >
              {nextLabel}
            </PrimaryButton>
          </Row>
          {consentLine && <ConsentLine>{consentLine}</ConsentLine>}
          {secureLine && (
            <SecureLine>
              <LockFilled />
              {secureLine}
            </SecureLine>
          )}
        </Inner>
      </Bar>
    </>
  );
};

export default ActionBar;
